import React, { FC, useRef, useState, useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { Icon } from '@packages/ui/shared';
import { RetailerSearchResult } from '@packages/models/api';
import { Typeahead } from 'react-bootstrap-typeahead';
import colors from '@packages/core/styles/colors';

export interface SearchRetailerProps {
    allRetailers: RetailerSearchResult[];
    canClearSelection?: boolean;
    clearSelection: () => Promise<void>;
    containerClassName?: string;
    fetchRetailers?: (text: string) => void;
    iconName?: string;
    labelKey?: (option: RetailerSearchResult) => string;
    placeholder?: string;
    recentSearch: () => Promise<void>;
    selected: RetailerSearchResult[];
    setSelected: (selected: RetailerSearchResult[]) => void;
}

const useStyles = createUseStyles({
    clearIcon: {
        backgroundColor: colors.white,
        cursor: 'pointer',
        position: 'absolute',
        right: 8,
        top: 8,
        zIndex: 2,
    },
});

export const SearchRetailer: FC<SearchRetailerProps> = ({
    allRetailers,
    canClearSelection = true,
    clearSelection,
    containerClassName,
    fetchRetailers,
    iconName = 'chevron-down',
    labelKey = (option) => `${option.retailerName} (#${option.retailerNumber})`,
    placeholder = 'Search Retailer',
    recentSearch,
    selected,
    setSelected,
}) => {
    const classes = useStyles();
    const typeaheadRef = useRef<any>(null);
    const [oldSelect, setOldSelect] = useState<string>('');

    const handleInputChange = useCallback(
        async (text: string) => {
            fetchRetailers?.(text);
        },
        [fetchRetailers]
    );

    const handleFocus = useCallback(async () => {
        const n = typeaheadRef.current?.inputNode;
        if (n) n.value = '';
    }, []);

    const handleClearSelection = useCallback(async () => {
        if (!canClearSelection) {
            return;
        }
        await clearSelection();
        setOldSelect('');
        const r = typeaheadRef.current;
        if (r) {
            r.clear();
            r.toggleMenu();
            r.inputNode?.focus?.();
        }
    }, [canClearSelection, clearSelection]);

    const handleSelectionChange = useCallback(
        (next: RetailerSearchResult[]) => {
            const isUserSelection = next.length > 0;
            setSelected(next);
            recentSearch();

            if (typeaheadRef.current && isUserSelection) {
                typeaheadRef.current.inputNode?.blur?.();
                setOldSelect(labelKey(next[0]));
            }
        },
        [labelKey, recentSearch, setSelected]
    );

    const handleToggleMenu = useCallback(() => {
        const r = typeaheadRef.current;
        if (r) {
            r.toggleMenu();
            r.inputNode?.focus?.();
        }
    }, []);

    const handleBlur = useCallback(() => {
        recentSearch();

        const r = typeaheadRef.current;
        if (!r) return;

        const inputValue = r.inputNode?.value ?? '';
        if (inputValue === '') return;

        if (selected.length > 0) {
            const selectedLabel = labelKey(selected[0]);
            r.setState?.({ text: selectedLabel });
        } else {
            r.setState?.({ text: oldSelect });
        }
    }, [labelKey, oldSelect, recentSearch, selected]);

    const hasValue = (typeaheadRef.current?.inputNode?.value?.length ?? 0) > 0;
    const shouldShowClearIcon = canClearSelection && hasValue;

    return (
        <div className={containerClassName}>
            <Typeahead
                filterBy={() => true}
                id="search-retailer"
                labelKey={labelKey}
                onBlur={handleBlur}
                onChange={handleSelectionChange}
                onFocus={handleFocus}
                onInputChange={handleInputChange}
                options={allRetailers}
                placeholder={placeholder}
                ref={typeaheadRef}
                selected={selected}
            >
                <div className={classes.clearIcon}>
                    {shouldShowClearIcon ? (
                        <Icon name="x-close" onPress={handleClearSelection} />
                    ) : (
                        <Icon name={iconName} onPress={handleToggleMenu} />
                    )}
                </div>
            </Typeahead>
        </div>
    );
};
